import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {signInWithEmailAndPassword, setPersistence, browserSessionPersistence, browserLocalPersistence} from "firebase/auth";
import {auth} from "./firebase"
import { usePageTitle } from "./hooks/usePageTitle";



// https://regexr.com/3bfsi
// const loginPatternPassword: RegExp = new RegExp('^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,127}$')

function Login() {
  usePageTitle('Login')
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  // @ts-ignore
  const [lastLogin, setLastLogin] = useState<string | null>(null);
  
  // login states
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  
  useEffect(() => {
      const last = localStorage.getItem("ts_last_login");
      if (last) setLastLogin(last);
    }, []);
    
    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
      // stop default form behaviour so we can do ajax stuff
      e.preventDefault();
      // set logic component states
      setError(null);
      setLoading(true);
      
      const form = e.currentTarget as HTMLFormElement;
      const form_data = new FormData(form);
      
      const username = String(form_data.get("username") || "");
      const password = String(form_data.get("password") || "");
      // get remember me status from use effect as its not part of form.
    const remember = Boolean(rememberMe); 
    
    // Sanitization Calls
    // let [fmt_username, ok_username] = loginSanitizeUsername(username);
    let [fmt_username, ok_username] = [username, true];
    let [fmt_password, ok_password] = [password, true];
    
    if(ok_username == false || ok_password == false) {
      setError("Username or Password is invalid.");
      setLoading(false);
      return;
    }
    
    try {
      // Set persistence based on remember me
        await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
        
        // Sign in with Firebase
        const userCredential = await signInWithEmailAndPassword(auth, fmt_username, fmt_password);
        const token = await userCredential.user.getIdToken();
        // Post token to server for verification
        const res = await fetch("/api/login", {
          method: "POST",
          headers: { "Content-Type": "application/json"},
          credentials: "include",
          body: JSON.stringify({ token })
        });
        
        // error route
        if(!res.ok) {
            const body = await res.json().catch(() => ({}));
            setError(body?.error || `Login failed (${res.status})`);
            setLoading(false);
            return;
        }

        // if okay set session cookies
        const now = new Date().toLocaleString();
        localStorage.setItem("ts_last_login", now);
        localStorage.setItem("ts_signedin", "true");
        localStorage.setItem("ts_username", fmt_username);
        setLastLogin(now);
        setLoading(false);
        navigate("/");
    } catch(err: any) {
        const firebaseErrorMap: Record<string, string> = {
          "auth/invalid-credential": "Incorrect email or password.",
          "auth/user-not-found": "Incorrect email or password.",
          "auth/wrong-password": "Incorrect email or password.",
          "auth/invalid-email": "Please enter a valid email address.",
          "auth/user-disabled": "This account has been disabled please contact support.",
          "auth/too-many-requests": "Too many failed attempts. Please try again later.",
          "auth/network-request-failed": "A network error occured. Please check your connection.",
        }   
        const message = firebaseErrorMap[err.code] ?? "An unexpected error occurred. Please try again.";
        setError(message);
        setLoading(false);
    }
  }

  return (
    <div
      id="ts-login-wrapper"
      className="w-full h-full p-8 flex flex-col items-center justify-center"
    >
      <div
        id="ts-login-card"
        className="w-1/3 h-fit p-4 flex flex-col rounded-sm items-center justify-center border-light-border border shadow-sm"
      >
        <div id="ts-login-title" className="text-2xl font-bold mt-4">
          Login
        </div>
        <div id="ts-login-subtitle" className="text-normal text-center p-2">
          Sign in to your TerraSciences Account
        </div>
        <form
          name="tslogin"
          method="POST"
          id="ts-login-form"
          className="w-full h-full p-4"
          onSubmit={handleSubmit}
        >
          <div className="ts-login-form-wrapper w-full h-fit flex flex-col items-center justify-center space-y-4">
            <div
              id="ts-login-form-group-username"
              className="w-full flex flex-col"
            >
              <label htmlFor="ts-login-form-username">Email</label>
              <input
                className="w-full p-2 border-light-border border focus:border-light-main"
                type="text"
                id="ts-login-form-username"
                name="username"
                placeholder="Enter your email"
                aria-label="Email"
                required
              />
              <div className="text-sm p-2 text-light-text-link">
                <a href="/login/reset-email">Forgot your Email?</a>
              </div>
            </div>
            <div
              id="ts-login-form-group-password"
              className="w-full flex flex-col"
            >
              <label htmlFor="ts-login-form-password">Password</label>
              <input
                className="w-full p-2 border-light-border border focus:border-light-main"
                type="password"
                id="ts-login-form-password"
                name="password"
                placeholder="Enter your password"
                aria-label="Password"
                required
              />
              <div className="text-sm p-2 text-light-text-link">
                <a href="/login/reset-password">Forgot your Password?</a>
              </div>
            </div>
            <div className="w-full flex items-center justify-between px-1">
              <label className="inline-flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <a className="text-sm text-light-text-link" href="/help/login">
                Need help?
              </a>
            </div>
            {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}
            <button
              id="ts-login-form-submit"
              className="w-1/2 rounded-sm p-2 cursor-pointer bg-light-main text-light-text-alt disabled:opacity-60"
              type="submit"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </div>
        </form>
      </div>
      <div className="w-1/2 relative mt-4 flex items-center justify-center p-4">
        <span>
          Looking to apply for an account? See{" "}
          <a className="text-light-text-link" href="/sales-faq">
            Sales FAQ
          </a>{" "}
          &{" "}
          <a className="text-light-text-link" href="/about-us/contact-us">
            Contact Us
          </a>
        </span>
      </div>
    </div>
  );
}

export default Login;
